import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../auth/AuthProvider.tsx';
import { useSpotifyClient } from '../spotify/useSpotifyClient.ts';
import { getCurrentUser, getDevices, getPlaybackState, pause, play, seek } from '../spotify/api.ts';
import { SpotifyApiError } from '../spotify/client.ts';
import type { SpotifyPlaybackState, SpotifyUser } from '../spotify/types.ts';
import { getRedirectUri } from '../config.ts';
import { loadSettings } from '../data/settings.ts';
import { resolveTargetDevice } from '../spotify/deviceResolver.ts';
import { DeviceSettings } from '../admin/DeviceSettings.tsx';
import { formatDuration, isContextUri, normalizeSpotifyUri } from '../shared/spotifyUri.ts';

const POLL_INTERVAL_MS = 5000;

function describeError(err: unknown): string {
  if (err instanceof SpotifyApiError) {
    if (err.isNoActiveDevice) {
      return 'Kein aktives Spotify-Gerät gefunden. Bitte prüfen, ob der Lautsprecher im Kinderzimmer erreichbar ist.';
    }
    if (err.isForbidden) {
      return `Nicht erlaubt (403). Meist fehlt Spotify Premium. Details: ${err.message}`;
    }
    return `${err.message} (HTTP ${err.status})`;
  }
  return err instanceof Error ? err.message : String(err);
}

/**
 * Einrichtung und Diagnose: prueft Schritt fuer Schritt, ob Anmeldung,
 * Premium, Geraet und der Sprung mitten in eine Folge funktionieren.
 */
export function DiagnosticsScreen() {
  const { status, clientId, setClientId, login, logout, error: authError } = useAuth();
  const client = useSpotifyClient();

  const [clientIdDraft, setClientIdDraft] = useState(clientId);
  const [user, setUser] = useState<SpotifyUser | null>(null);
  const [playback, setPlayback] = useState<SpotifyPlaybackState | null>(null);
  const [testUri, setTestUri] = useState('');
  const [trackNumber, setTrackNumber] = useState('1');
  const [startSeconds, setStartSeconds] = useState('30');
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const loggedIn = status === 'loggedIn';
  const redirectUri = getRedirectUri();
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const run = useCallback(async (action: () => Promise<void>) => {
    setBusy(true);
    setMessage(null);
    try {
      await action();
    } catch (err) {
      if (mountedRef.current) setMessage(describeError(err));
    } finally {
      if (mountedRef.current) setBusy(false);
    }
  }, []);

  /**
   * Ermittelt das Zielgeraet genauso wie die App im Betrieb – der Test soll
   * das echte Verhalten pruefen, nicht eine Sonderauswahl.
   */
  const resolveDeviceId = useCallback(async (): Promise<string | undefined> => {
    const settings = loadSettings();
    const devices = await getDevices(client);
    const resolved = resolveTargetDevice(devices, settings.primaryDevice, settings.fallbackDevice);
    if (!resolved?.device.id) {
      throw new Error(
        'Kein steuerbares Gerät. Bitte in Schritt 3 ein Standardgerät festlegen.',
      );
    }
    return resolved.device.id;
  }, [client]);

  // Profil laden, sobald angemeldet.
  useEffect(() => {
    if (!loggedIn) return;
    void run(async () => {
      const profile = await getCurrentUser(client);
      if (mountedRef.current) setUser(profile);
    });
  }, [loggedIn, client, run]);

  // Wiedergabestatus pollen, solange die Seite sichtbar ist.
  useEffect(() => {
    if (!loggedIn) return;

    let timer: number | undefined;
    let cancelled = false;

    const tick = async () => {
      if (document.visibilityState !== 'visible') return;
      try {
        const state = await getPlaybackState(client);
        if (!cancelled && mountedRef.current) setPlayback(state);
      } catch {
        // Polling-Fehler bewusst still – der Nutzer sieht Fehler nur bei Aktionen.
      }
    };

    void tick();
    timer = window.setInterval(() => void tick(), POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      if (timer) window.clearInterval(timer);
    };
  }, [loggedIn, client]);

  const handleSaveClientId = () => {
    setClientId(clientIdDraft);
    setMessage('Client ID gespeichert.');
  };

  const copyRedirectUri = () => {
    void navigator.clipboard
      .writeText(redirectUri)
      .then(() => setMessage('Redirect-URI kopiert.'))
      .catch(() => setMessage(`Bitte manuell kopieren: ${redirectUri}`));
  };

  const handlePlayTest = () =>
    run(async () => {
      if (!testUri.trim()) {
        throw new Error(
          'Bitte zuerst einen Spotify-Link einfügen (in der Spotify-App: Teilen → Link kopieren).',
        );
      }
      const uri = normalizeSpotifyUri(testUri);
      if (!uri) {
        throw new Error(
          'Das sieht nicht nach einem Spotify-Link aus. Erwartet wird z. B. https://open.spotify.com/album/… oder spotify:album:…',
        );
      }

      const positionMs = Math.max(0, Number(startSeconds) || 0) * 1000;
      // Kapitel sind 1-basiert dargestellt, die API erwartet einen 0-basierten Offset.
      const offsetPosition = Math.max(1, Number(trackNumber) || 1) - 1;
      const useOffset = isContextUri(uri);

      await play(client, {
        deviceId: await resolveDeviceId(),
        ...(useOffset ? { contextUri: uri, offsetPosition } : { uris: [uri] }),
        positionMs,
      });

      setMessage(
        useOffset
          ? `Gestartet: Kapitel ${offsetPosition + 1} ab ${formatDuration(positionMs)}.`
          : `Gestartet ab ${formatDuration(positionMs)}.`,
      );
    });

  const currentItemName = playback?.item?.name ?? '–';
  // Nur Tracks (Album-Kapitel) haben eine Titelnummer, Podcast-Episoden nicht.
  const currentTrackNumber =
    playback?.item && 'track_number' in playback.item ? playback.item.track_number : null;

  const captureCurrentPosition = () => {
    if (!playback?.context?.uri) return;
    setTestUri(playback.context.uri);
    setTrackNumber(String(currentTrackNumber ?? 1));
    setStartSeconds(String(Math.floor((playback.progress_ms ?? 0) / 1000)));
    setMessage('Aktuelle Stelle in Schritt 4 übernommen.');
  };

  return (
    <div className="diag">
      <header className="diag__header">
        <h1>Spotibox – Einrichtung &amp; Diagnose</h1>
        <p className="diag__lead">
          Phase 0: prüfen, ob sich die Spotify-App auf diesem Gerät per Spotify Connect
          fernsteuern lässt.
        </p>
      </header>

      {(message ?? authError) && (
        <p className="diag__message" role="status">
          {message ?? authError}
        </p>
      )}

      <section className="card">
        <h2>1. Spotify-App verbinden</h2>
        <ol className="steps">
          <li>
            Im{' '}
            <a
              href="https://developer.spotify.com/dashboard"
              target="_blank"
              rel="noreferrer noopener"
            >
              Spotify Developer Dashboard
            </a>{' '}
            eine App anlegen.
          </li>
          <li>
            Als <strong>Redirect URI</strong> exakt eintragen:
            <div className="copyrow">
              <code>{redirectUri}</code>
              <button type="button" onClick={copyRedirectUri}>
                Kopieren
              </button>
            </div>
          </li>
          <li>
            Unter „APIs used" <strong>Web API</strong> auswählen.
          </li>
          <li>Die Client ID hier eintragen:</li>
        </ol>

        <div className="field">
          <label htmlFor="clientId">Client ID</label>
          <input
            id="clientId"
            value={clientIdDraft}
            onChange={(event) => setClientIdDraft(event.target.value)}
            placeholder="z. B. 4f2c1a…"
            autoComplete="off"
            spellCheck={false}
          />
          <button type="button" onClick={handleSaveClientId} disabled={!clientIdDraft.trim()}>
            Speichern
          </button>
        </div>

        <div className="actions">
          {loggedIn ? (
            <button type="button" onClick={logout}>
              Abmelden
            </button>
          ) : (
            <button
              type="button"
              className="primary"
              onClick={() => void login()}
              disabled={!clientId}
            >
              Mit Spotify anmelden
            </button>
          )}
          <StatusPill ok={loggedIn} label={loggedIn ? 'Angemeldet' : 'Nicht angemeldet'} />
        </div>
      </section>

      <section className="card" aria-disabled={!loggedIn}>
        <h2>2. Konto prüfen</h2>
        {user ? (
          <ul className="facts">
            <li>
              <span>Konto</span>
              <strong>{user.display_name ?? user.id}</strong>
            </li>
            <li>
              <span>Abo</span>
              <strong>{user.product ?? 'unbekannt'}</strong>
            </li>
            <li>
              <span>Land</span>
              <strong>{user.country ?? '–'}</strong>
            </li>
          </ul>
        ) : (
          <p className="muted">Noch keine Kontodaten geladen.</p>
        )}
        <StatusPill
          ok={user?.product === 'premium'}
          label={
            user?.product === 'premium'
              ? 'Premium vorhanden – Fernsteuerung möglich'
              : 'Premium erforderlich (ohne Premium blockiert Spotify die Steuerung)'
          }
        />
      </section>

      <section className="card">
        <h2>3. Abspielgerät festlegen</h2>
        <DeviceSettings onMessage={setMessage} />
      </section>

      <section className="card">
        <h2>4. Sprung mitten in eine Folge testen</h2>
        <p className="muted">
          Der entscheidende Test: Startet Spotify die Folge an der gewünschten Position? Davon
          hängt die „Weiterhören"-Funktion ab. Hörspiel-Alben bestehen meist aus vielen kurzen
          Kapitel-Tracks – die Minuten gelten immer <strong>innerhalb</strong> des gewählten
          Kapitels.
        </p>

        <div className="field">
          <label htmlFor="testUri">Spotify-Link oder URI</label>
          <input
            id="testUri"
            value={testUri}
            onChange={(event) => setTestUri(event.target.value)}
            placeholder="https://open.spotify.com/album/… oder spotify:album:…"
            autoComplete="off"
            spellCheck={false}
          />
        </div>

        <div className="field">
          <label htmlFor="trackNumber">Kapitel / Titel-Nr.</label>
          <input
            id="trackNumber"
            type="number"
            min="1"
            step="1"
            value={trackNumber}
            onChange={(event) => setTrackNumber(event.target.value)}
          />
        </div>

        <div className="field">
          <label htmlFor="startSeconds">Startposition im Kapitel (Sekunden)</label>
          <input
            id="startSeconds"
            type="number"
            min="0"
            step="1"
            value={startSeconds}
            onChange={(event) => setStartSeconds(event.target.value)}
          />
        </div>

        <div className="actions">
          <button
            type="button"
            className="primary"
            onClick={() => void handlePlayTest()}
            disabled={!loggedIn || busy}
          >
            Abspielen ab Position
          </button>
          <button
            type="button"
            onClick={() =>
              void run(async () => {
                await pause(client, await resolveDeviceId());
                setMessage('Pausiert.');
              })
            }
            disabled={!loggedIn || busy}
          >
            Pause
          </button>
          <button
            type="button"
            onClick={() =>
              void run(async () => {
                await play(client, { deviceId: await resolveDeviceId() });
                setMessage('Weiter.');
              })
            }
            disabled={!loggedIn || busy}
          >
            Weiter
          </button>
          <button
            type="button"
            onClick={() =>
              void run(async () => {
                const current = playback?.progress_ms ?? 0;
                await seek(client, current + 30_000, await resolveDeviceId());
                setMessage('30 Sekunden vorgespult.');
              })
            }
            disabled={!loggedIn || busy}
          >
            +30 s
          </button>
        </div>
      </section>

      <section className="card">
        <h2>5. Live-Status</h2>
        <ul className="facts">
          <li>
            <span>Läuft</span>
            <strong>{playback?.is_playing ? 'ja' : 'nein'}</strong>
          </li>
          <li>
            <span>Titel</span>
            <strong>{currentItemName}</strong>
          </li>
          <li>
            <span>Kapitel</span>
            <strong>{currentTrackNumber ?? '–'}</strong>
          </li>
          <li>
            <span>Position</span>
            <strong>
              {formatDuration(playback?.progress_ms ?? null)} /{' '}
              {formatDuration(playback?.item?.duration_ms ?? null)}
            </strong>
          </li>
          <li>
            <span>Gerät</span>
            <strong>{playback?.device?.name ?? '–'}</strong>
          </li>
          <li>
            <span>Kontext</span>
            <strong>{playback?.context?.uri ?? '–'}</strong>
          </li>
        </ul>
        <div className="actions">
          <button type="button" onClick={captureCurrentPosition} disabled={!playback?.context}>
            Diese Stelle in den Test übernehmen
          </button>
        </div>
        <p className="muted">
          So lässt sich „Weiterhören" realistisch prüfen: Stelle übernehmen, Spotify beenden oder
          etwas anderes abspielen – und dann in Schritt 4 zurückspringen.
        </p>
        <p className="muted">Aktualisierung alle {POLL_INTERVAL_MS / 1000} Sekunden.</p>
      </section>
    </div>
  );
}

function StatusPill({ ok, label }: { ok: boolean; label: string }) {
  return (
    <p className={ok ? 'pill pill--ok' : 'pill pill--warn'}>
      <span aria-hidden="true">{ok ? '✓' : '!'}</span> {label}
    </p>
  );
}
